import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import Layout from '../../../layouts';
import Page from '../../../components/Page';
import useAuth from '../../../hooks/useAuth';
import axios from '../../../utils/axios';
import { isMaster } from '../../../utils/perfil';
import { Num } from '../../../components/gps';
import Modal, { AreaArquivo, BotaoPrimario, BotaoSecundario, Campo, classesEntrada } from '../../../components/gps/Modal';
import { cn } from '../../../lib/utils';
import TituloTela from '../../../components/gps/TituloTela';

/* ============================================================================
   Mural de avisos.

   DUAS TELAS NO MESMO LUGAR, porque as perguntas sao diferentes:

     - QUEM LE ve CARTOES: um aviso e um texto para ler, nao um registro para
       comparar. Cartao deixa o texto respirar e cabe mensagem de tamanho
       irregular, que e o que acontece quando alguem escreve a mao.

     - O MASTER ve TABELA: ele nao esta lendo, esta administrando - precisa
       comparar vigencia, fixado e quantas pessoas leram, o que so funciona em
       colunas alinhadas. E aqui ele tambem publica, edita e remove.

   O Master pode alternar entre as duas visoes: ele tambem e leitor do mural.

   A separacao NAO e so visual: `GET /avisos` devolve apenas o que esta vigente,
   enquanto `GET /avisos/admin` traz tambem o programado e o expirado - e recusa
   quem nao e Master, no servidor.
   ============================================================================ */

type Aviso = {
  codigo: number;
  titulo: string;
  mensagem: string;
  tipo: 'INFORMACAO' | 'ATENCAO' | 'URGENTE' | string;
  fixado: boolean;
  inicioEm: string | null;
  fimEm: string | null;
  anexoNome: string | null;
  createdAt: string;
  autorNome: string | null;
  lido?: boolean;
  leituras?: number;
};

const TIPOS = [
  { valor: 'INFORMACAO', rotulo: 'Informação' },
  { valor: 'ATENCAO', rotulo: 'Atenção' },
  { valor: 'URGENTE', rotulo: 'Urgente' },
];

/* Cor por tipo. Aqui a cor CARREGA SIGNIFICADO - urgência -, e por isso sai dos
   tokens semânticos, os mesmos que o resto do sistema usa para erro e alerta.
   Um aviso urgente pintado com a cor de "informação" seria uma mentira visual. */
const TOM: Record<string, { faixa: string; selo: string; rotulo: string }> = {
  INFORMACAO: { faixa: 'bg-info', selo: 'bg-info-soft text-info', rotulo: 'Informação' },
  ATENCAO: { faixa: 'bg-warn', selo: 'bg-warn-soft text-warn', rotulo: 'Atenção' },
  URGENTE: { faixa: 'bg-danger', selo: 'bg-danger-soft text-danger', rotulo: 'Urgente' },
};

function tom(tipo: string) {
  return TOM[tipo] || TOM.INFORMACAO;
}

function data(valor?: string | null) {
  if (!valor) return '—';
  const d = new Date(valor);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(d);
}

/** `datetime-local` exige `YYYY-MM-DDTHH:mm` - e sem fuso, senão o campo ignora. */
function paraCampoData(valor?: string | null) {
  if (!valor) return '';
  const d = new Date(valor);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

AvisosPage.getLayout = function getLayout(page: React.ReactElement) {
  return <Layout>{page}</Layout>;
};

export default function AvisosPage() {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const master = isMaster(user);

  const [visao, setVisao] = useState<'mural' | 'gerenciar'>('mural');
  const [lista, setLista] = useState<Aviso[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  // Formulario (Master)
  const [editando, setEditando] = useState<Aviso | null>(null);
  const [criando, setCriando] = useState(false);
  const [titulo, setTitulo] = useState('');
  const [mensagem, setMensagem] = useState('');
  const [tipo, setTipo] = useState('INFORMACAO');
  const [fixado, setFixado] = useState(false);
  const [inicioEm, setInicioEm] = useState('');
  const [fimEm, setFimEm] = useState('');
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [removendo, setRemovendo] = useState<Aviso | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      const rota = visao === 'gerenciar' ? '/avisos/admin' : '/avisos';
      const { data: resposta } = await axios.get<{ avisos: Aviso[] }>(rota);
      setLista(Array.isArray(resposta?.avisos) ? resposta.avisos : []);
    } catch (e: any) {
      setErro(e?.response?.data?.message || 'Não foi possível carregar os avisos.');
      setLista([]);
    } finally {
      setCarregando(false);
    }
  }, [visao]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const naoLidos = useMemo(() => lista.filter((a) => a.lido === false).length, [lista]);

  function abrirNovo() {
    setEditando(null);
    setTitulo('');
    setMensagem('');
    setTipo('INFORMACAO');
    setFixado(false);
    setInicioEm('');
    setFimEm('');
    setArquivo(null);
    setCriando(true);
  }

  function abrirEdicao(a: Aviso) {
    setEditando(a);
    setTitulo(a.titulo);
    setMensagem(a.mensagem);
    setTipo(a.tipo);
    setFixado(Boolean(a.fixado));
    setInicioEm(paraCampoData(a.inicioEm));
    setFimEm(paraCampoData(a.fimEm));
    setArquivo(null);
    setCriando(true);
  }

  const valido = titulo.trim().length > 0 && mensagem.trim().length > 0;

  async function salvar() {
    if (!valido || salvando) return;
    setSalvando(true);
    try {
      /* FormData sempre, com ou sem arquivo: o endpoint aceita multipart por causa
         do anexo, e mandar JSON em um caso e multipart no outro cria dois caminhos
         de leitura no servidor para a mesma operacao. */
      const corpo = new FormData();
      corpo.append('titulo', titulo.trim());
      corpo.append('mensagem', mensagem.trim());
      corpo.append('tipo', tipo);
      corpo.append('fixado', String(fixado));
      /* Na EDICAO o campo vazio vai como '' (o servidor converte em null): so
         omitir fazia o servidor manter a data antiga - apagar o fim de um aviso
         e salvar dizia "atualizado" e o aviso continuava expirando. */
      if (inicioEm) corpo.append('inicioEm', new Date(inicioEm).toISOString());
      else if (editando) corpo.append('inicioEm', '');
      if (fimEm) corpo.append('fimEm', new Date(fimEm).toISOString());
      else if (editando) corpo.append('fimEm', '');
      if (arquivo) corpo.append('file', arquivo);

      if (editando) await axios.put(`/avisos/${editando.codigo}`, corpo);
      else await axios.post('/avisos', corpo);

      enqueueSnackbar(editando ? 'Aviso atualizado.' : 'Aviso publicado.', { variant: 'success' });
      setCriando(false);
      carregar();
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Não foi possível salvar o aviso.', { variant: 'error' });
    } finally {
      setSalvando(false);
    }
  }

  async function remover() {
    if (!removendo) return;
    try {
      await axios.delete(`/avisos/${removendo.codigo}`);
      enqueueSnackbar('Aviso removido.', { variant: 'success' });
      setRemovendo(null);
      carregar();
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Não foi possível remover.', { variant: 'error' });
    }
  }

  async function marcarLido(a: Aviso) {
    // Atualiza a tela ANTES da resposta: marcar como lido nao pode dar sensacao de
    // lentidao, e se falhar o proximo carregamento devolve o estado real.
    setLista((atual) => atual.map((x) => (x.codigo === a.codigo ? { ...x, lido: true } : x)));
    try {
      await axios.post(`/avisos/${a.codigo}/lido`);
    } catch (e) {
      /* silencioso de proposito */
    }
  }

  async function marcarTodos() {
    setLista((atual) => atual.map((x) => ({ ...x, lido: true })));
    try {
      await axios.post('/avisos/lidos');
    } catch (e) {
      carregar();
    }
  }

  async function baixarAnexo(a: Aviso) {
    try {
      const { data: resposta } = await axios.get<{ anexo: string | null; nome: string | null }>(
        `/avisos/${a.codigo}/anexo`
      );
      if (!resposta?.anexo) {
        enqueueSnackbar('Este aviso não tem anexo.', { variant: 'warning' });
        return;
      }
      /* Ancora temporaria com o data URI: o anexo vem por rota autenticada (em
         base64), entao nao existe URL que o navegador possa baixar sozinho. */
      const link = document.createElement('a');
      link.href = resposta.anexo;
      link.download = resposta.nome || `aviso-${a.codigo}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Não foi possível baixar o anexo.', { variant: 'error' });
    }
  }

  return (
    <Page title="Avisos">
      <div className="mx-auto w-full max-w-[var(--gps-pagina-max)] px-4 pb-2 pt-2">
        <header className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <div>
            <TituloTela
              descricao={
                <>
{visao === 'mural'
                ? 'Comunicados da equipe de suporte. Os fixados ficam sempre no topo.'
                : 'Publique, edite e acompanhe quantas pessoas leram cada aviso. Aqui aparecem também os programados e os expirados.'}
                </>
              }
            >
              Avisos
            </TituloTela>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {visao === 'mural' && naoLidos > 0 && (
              <BotaoSecundario onClick={marcarTodos}>Marcar todos como lidos</BotaoSecundario>
            )}

            {master && (
              <>
                {/* Alternancia entre ler e administrar. O Master tambem e leitor -
                    forcar ele a ver a tabela para acompanhar o mural seria estranho. */}
                <div className="inline-flex rounded-lg border border-line bg-surface p-0.5">
                  {(['mural', 'gerenciar'] as const).map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setVisao(v)}
                      className={cn(
                        'h-8 rounded-md px-3 text-[12px] font-semibold',
                        visao === v ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-ink'
                      )}
                    >
                      {v === 'mural' ? 'Mural' : 'Gerenciar'}
                    </button>
                  ))}
                </div>

                <BotaoPrimario onClick={abrirNovo}>+ Novo aviso</BotaoPrimario>
              </>
            )}
          </div>
        </header>

        {carregando && (
          <div className="rounded-lg border border-line bg-surface p-8 text-center text-[12.5px] text-muted-foreground">
            Carregando avisos...
          </div>
        )}

        {!carregando && erro && (
          <div className="rounded-lg border border-danger bg-danger-soft p-4 text-[12.5px] text-danger">
            {erro}{' '}
            <button type="button" onClick={carregar} className="font-bold underline">
              tentar de novo
            </button>
          </div>
        )}

        {!carregando && !erro && lista.length === 0 && (
          <div className="rounded-lg border border-line bg-surface p-10 text-center shadow-card">
            <p className="text-[12.5px] text-muted-foreground">
              {visao === 'mural' ? 'Nenhum aviso publicado agora.' : 'Nenhum aviso cadastrado.'}
            </p>
          </div>
        )}

        {/* ------------------------------------------------------------ MURAL */}
        {!carregando && !erro && lista.length > 0 && visao === 'mural' && (
          <div className="grid gap-3 lg:grid-cols-2">
            {lista.map((a) => {
              const t = tom(a.tipo);
              return (
                <article
                  key={a.codigo}
                  className={cn(
                    'relative overflow-hidden rounded-lg border bg-surface p-4 shadow-card',
                    // Não lido ganha borda na cor do tipo: é o que precisa ser visto.
                    a.lido === false ? 'border-primary/40' : 'border-line'
                  )}
                >
                  <span className={cn('absolute inset-x-0 top-0 h-[3px]', t.faixa)} aria-hidden />

                  <div className="flex flex-wrap items-center gap-2">
                    <span className={cn('inline-flex h-[20px] items-center rounded px-1.5 text-[10px] font-bold uppercase tracking-wide', t.selo)}>
                      {t.rotulo}
                    </span>
                    {a.fixado && (
                      <span className="inline-flex h-[20px] items-center rounded bg-soft px-1.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                        fixado
                      </span>
                    )}
                    {a.lido === false && (
                      <span className="inline-flex items-center gap-1 text-[11px] font-bold text-primary">
                        <span className="h-1.5 w-1.5 rounded-full bg-primary" aria-hidden />
                        novo
                      </span>
                    )}
                    <Num className="ml-auto text-[11px] text-faint">{data(a.createdAt)}</Num>
                  </div>

                  <h2 className="mt-2 text-[14px] font-bold leading-snug">{a.titulo}</h2>

                  {/* `whitespace-pre-wrap`: quem escreve um aviso escreve em linhas,
                      e sem isto o texto vira um paragrafo unico. */}
                  <p className="mt-1 whitespace-pre-wrap text-[12px] leading-[1.6] text-muted-foreground">
                    {a.mensagem}
                  </p>

                  <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-line pt-2.5">
                    {a.autorNome && <span className="text-[11px] text-faint">por {a.autorNome}</span>}

                    {a.anexoNome && (
                      <button
                        type="button"
                        onClick={() => baixarAnexo(a)}
                        className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-primary hover:underline"
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden>
                          <path d="M12 4v11m0 0l-4-4m4 4l4-4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M4 17v1a3 3 0 003 3h10a3 3 0 003-3v-1" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                        </svg>
                        {a.anexoNome}
                      </button>
                    )}

                    {a.lido === false && (
                      <button
                        type="button"
                        onClick={() => marcarLido(a)}
                        className="ml-auto h-7 rounded-md border border-line px-2 text-[11px] font-semibold text-muted-foreground hover:text-ink"
                      >
                        marcar como lido
                      </button>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}

        {/* -------------------------------------------------------- GERENCIAR */}
        {!carregando && !erro && lista.length > 0 && visao === 'gerenciar' && (
          <div className="overflow-hidden rounded-lg border border-primary/25 bg-surface shadow-card">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-[12px]">
                <thead>
                  <tr className="border-b border-primary bg-primary text-[11px] uppercase tracking-[0.07em] text-primary-foreground">
                    <th className="px-3 py-2 text-left font-bold">Aviso</th>
                    <th className="px-3 py-2 text-left font-bold">Tipo</th>
                    <th className="px-3 py-2 text-left font-bold">Vigência</th>
                    <th className="px-3 py-2 text-right font-bold">Leituras</th>
                    <th className="px-3 py-2 text-right font-bold">Opções</th>
                  </tr>
                </thead>
                <tbody>
                  {lista.map((a) => {
                    const t = tom(a.tipo);
                    const expirado = a.fimEm ? new Date(a.fimEm).getTime() < Date.now() : false;
                    const programado = a.inicioEm ? new Date(a.inicioEm).getTime() > Date.now() : false;

                    return (
                      <tr key={a.codigo} className="border-b border-line align-top last:border-0 hover:bg-surface-2">
                        <td className="px-3 py-2">
                          <span className="block font-semibold">{a.titulo}</span>
                          <span className="block max-w-[52ch] truncate text-[11px] text-muted-foreground">
                            {a.mensagem}
                          </span>
                          <span className="mt-0.5 flex flex-wrap items-center gap-2">
                            {a.fixado && <span className="text-[10px] font-bold uppercase text-muted-foreground">fixado</span>}
                            {a.anexoNome && (
                              <button
                                type="button"
                                onClick={() => baixarAnexo(a)}
                                className="text-[10px] font-semibold text-primary hover:underline"
                              >
                                {a.anexoNome}
                              </button>
                            )}
                          </span>
                        </td>

                        <td className="px-3 py-2">
                          <span className={cn('inline-flex h-[19px] items-center rounded px-1.5 text-[10px] font-bold', t.selo)}>
                            {t.rotulo}
                          </span>
                        </td>

                        <td className="px-3 py-2 text-[11px] text-muted-foreground">
                          {/* Estado vem antes das datas: "expirado" e a informacao
                              que muda a decisao; a data exata e o detalhe. */}
                          {expirado && <span className="block font-bold text-danger">expirado</span>}
                          {programado && <span className="block font-bold text-warn">programado</span>}
                          {!expirado && !programado && <span className="block font-bold text-ok">vigente</span>}
                          <Num className="block text-[11px] text-faint">
                            {a.inicioEm ? data(a.inicioEm) : 'desde já'} → {a.fimEm ? data(a.fimEm) : 'sem fim'}
                          </Num>
                        </td>

                        <td className="px-3 py-2 text-right">
                          <Num className="font-bold">{a.leituras ?? 0}</Num>
                        </td>

                        <td className="px-3 py-2 text-right">
                          <span className="inline-flex gap-1.5">
                            <button
                              type="button"
                              onClick={() => abrirEdicao(a)}
                              className="h-7 rounded-md border border-line px-2 text-[11px] font-semibold text-muted-foreground hover:text-ink"
                            >
                              editar
                            </button>
                            <button
                              type="button"
                              onClick={() => setRemovendo(a)}
                              className="h-7 rounded-md border border-danger px-2 text-[11px] font-bold text-danger"
                            >
                              remover
                            </button>
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* --------------------------------------------------------- FORMULARIO */}
      <Modal
        aberto={criando}
        titulo={editando ? 'Editar aviso' : 'Novo aviso'}
        descricao={editando ? editando.titulo : 'Publicado para todos os usuários do sistema.'}
        largura="max-w-[640px]"
        onFechar={() => setCriando(false)}
        rodape={
          <>
            <BotaoSecundario onClick={() => setCriando(false)} disabled={salvando}>
              Cancelar
            </BotaoSecundario>
            <BotaoPrimario onClick={salvar} disabled={!valido || salvando}>
              {salvando ? 'Salvando...' : editando ? 'Salvar' : 'Publicar'}
            </BotaoPrimario>
          </>
        }
      >
        <Campo rotulo="Título" obrigatorio>
          <input
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            maxLength={160}
            className={classesEntrada}
            placeholder="Ex.: Manutenção programada no sábado"
          />
        </Campo>

        <Campo rotulo="Mensagem" obrigatorio>
          <textarea
            value={mensagem}
            onChange={(e) => setMensagem(e.target.value)}
            rows={5}
            className={cn(classesEntrada, 'h-auto py-2 leading-[1.5]')}
            placeholder="O que as pessoas precisam saber, e o que devem fazer."
          />
        </Campo>

        <div className="grid gap-3 sm:grid-cols-2">
          <Campo rotulo="Tipo" dica="A cor do aviso vem daqui.">
            <select value={tipo} onChange={(e) => setTipo(e.target.value)} className={classesEntrada}>
              {TIPOS.map((t) => (
                <option key={t.valor} value={t.valor}>
                  {t.rotulo}
                </option>
              ))}
            </select>
          </Campo>

          <Campo rotulo="Fixar no topo" dica="Fica acima dos demais, independente da data.">
            <label className="inline-flex h-9 cursor-pointer items-center gap-2 text-[12px] font-semibold">
              <input
                type="checkbox"
                checked={fixado}
                onChange={(e) => setFixado(e.target.checked)}
                className="h-3.5 w-3.5 accent-[hsl(var(--primary))]"
              />
              {fixado ? 'Sim, fixado' : 'Não'}
            </label>
          </Campo>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <Campo rotulo="Começa em" dica="Vazio publica agora.">
            <input type="datetime-local" value={inicioEm} onChange={(e) => setInicioEm(e.target.value)} className={classesEntrada} />
          </Campo>
          <Campo rotulo="Termina em" dica="Vazio não expira.">
            <input type="datetime-local" value={fimEm} onChange={(e) => setFimEm(e.target.value)} className={classesEntrada} />
          </Campo>
        </div>

        <Campo bloco rotulo="Anexo" dica="Opcional — PDF, imagem, planilha ou documento, até 5 MB.">
          <AreaArquivo
            arquivo={arquivo}
            onArquivo={setArquivo}
            extensoes={['.pdf', '.png', '.jpg', '.jpeg', '.webp', '.txt', '.csv', '.xlsx', '.docx']}
            rotulo="Arraste o arquivo aqui ou clique para escolher"
            dica={editando?.anexoNome ? `Atual: ${editando.anexoNome}` : undefined}
          />
        </Campo>
      </Modal>

      <Modal
        aberto={Boolean(removendo)}
        titulo="Remover aviso?"
        descricao="Ele sai do mural de todos. O registro de quem já leu é preservado."
        icone="!"
        largura="max-w-[460px]"
        onFechar={() => setRemovendo(null)}
        rodape={
          <>
            <BotaoSecundario onClick={() => setRemovendo(null)}>Cancelar</BotaoSecundario>
            <button
              type="button"
              onClick={remover}
              className="h-9 rounded-lg bg-danger px-4 text-[12.5px] font-bold text-white"
            >
              Remover
            </button>
          </>
        }
      >
        {removendo && (
          <div className="rounded-lg border border-line bg-surface-2 p-3">
            <span className="block text-[12px] font-bold">{removendo.titulo}</span>
            <span className="mt-0.5 block text-[11px] text-muted-foreground">
              <Num>{removendo.leituras ?? 0}</Num> leitura(s) registrada(s)
            </span>
          </div>
        )}
      </Modal>
    </Page>
  );
}
